import React, { useState, useEffect, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  Settings,
  Code,
  Storage,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    gap: theme.spacing(1),
    "& > *": {
      flex: 1,
    },
  },
  tabPanel: {
    padding: theme.spacing(2),
  },
  queryEditor: {
    fontFamily: "monospace",
    fontSize: "14px",
  },
  dbCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5),
    cursor: "pointer",
    transition: "all 0.2s ease",
    textAlign: "center",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  dbCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.02)",
  },
}));

// Tipos de banco de dados
const databaseTypes = [
  { id: "postgres", name: "PostgreSQL", color: "#336791" },
  { id: "mysql", name: "MySQL", color: "#4479A1" },
  { id: "sqlite", name: "SQLite", color: "#003B57" },
  { id: "mssql", name: "SQL Server", color: "#CC2927" },
];

// Tipos de query
const queryTypes = [
  { id: "select", name: "SELECT", description: "Buscar dados" },
  { id: "insert", name: "INSERT", description: "Inserir dados" },
  { id: "update", name: "UPDATE", description: "Atualizar dados" },
  { id: "delete", name: "DELETE", description: "Remover dados" },
  { id: "custom", name: "Personalizado", description: "Query customizada" },
];

const FlowBuilderAddSQLModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);

  const initialState = {
    queryName: "",
    dbType: "postgres",
    queryType: "select",
    // Conexão
    host: "localhost",
    port: "5432",
    database: "",
    username: "",
    password: "",
    // Query
    query: "",
    tableName: "",
    columns: "*",
    whereClause: "",
    // Resultado
    outputVariable: "sqlResult",
    returnFirst: false,
    timeout: 30000,
  };

  const [sqlConfig, setSqlConfig] = useState(initialState);
  const [labels, setLabels] = useState({
    title: "Adicionar Consulta SQL ao fluxo",
    btn: "Adicionar",
  });
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Consulta SQL",
        btn: "Salvar",
      });
      const config = data?.data?.sqlConfig || {};
      setSqlConfig({
        ...initialState,
        ...config,
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Consulta SQL ao fluxo",
        btn: "Adicionar",
      });
      setSqlConfig(initialState);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    setActiveTab(0);
    close(null);
  };

  const handleSave = () => {
    if (!sqlConfig.queryName || !sqlConfig.database) {
      return;
    }

    // Gerar query baseado no tipo se não for custom
    let finalQuery = sqlConfig.query;
    if (sqlConfig.queryType !== "custom" && !finalQuery) {
      finalQuery = generateQuery(sqlConfig);
    }

    const configToSave = {
      ...sqlConfig,
      query: finalQuery,
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { sqlConfig: configToSave },
      });
    } else if (open === "create") {
      onSave({ sqlConfig: configToSave });
    }
    handleClose();
  };

  const generateQuery = (config) => {
    const { queryType, tableName, columns, whereClause } = config;

    switch (queryType) {
      case "select":
        return `SELECT ${columns || "*"} FROM ${tableName || "tabela"}${
          whereClause ? ` WHERE ${whereClause}` : ""
        }`;
      case "insert":
        return `INSERT INTO ${tableName || "tabela"} (${columns || "coluna1"}) VALUES (\${valor})`;
      case "update":
        return `UPDATE ${tableName || "tabela"} SET coluna = \${valor}${
          whereClause ? ` WHERE ${whereClause}` : ""
        }`;
      case "delete":
        return `DELETE FROM ${tableName || "tabela"}${
          whereClause ? ` WHERE ${whereClause}` : ""
        }`;
      default:
        return "";
    }
  };

  const getDefaultPort = (dbType) => {
    switch (dbType) {
      case "postgres":
        return "5432";
      case "mysql":
        return "3306";
      case "mssql":
        return "1433";
      case "sqlite":
        return "";
      default:
        return "5432";
    }
  };

  const handleDbTypeChange = (dbType) => {
    setSqlConfig({
      ...sqlConfig,
      dbType,
      port: getDefaultPort(dbType),
    });
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>{labels.title}</DialogTitle>
        <DialogContent dividers>
          {/* TABS */}
          <Paper square style={{ marginBottom: 16 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Conexão" icon={<Settings />} />
              <Tab label="Query" icon={<Code />} />
              <Tab label="Resultado" icon={<Storage />} />
            </Tabs>
          </Paper>

          {/* TAB 0: Conexão */}
          {activeTab === 0 && (
            <div className={classes.tabPanel}>
              <TextField
                label="Nome da Consulta"
                value={sqlConfig.queryName}
                onChange={(e) =>
                  setSqlConfig({ ...sqlConfig, queryName: e.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                required
                placeholder="Ex: Buscar Cliente, Atualizar Pedido..."
              />

              <Typography
                variant="subtitle1"
                style={{ marginTop: 16, marginBottom: 8, fontWeight: "bold" }}
              >
                Tipo de Banco de Dados
              </Typography>

              <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
                {databaseTypes.map((db) => (
                  <Box
                    key={db.id}
                    className={`${classes.dbCard} ${
                      sqlConfig.dbType === db.id ? classes.dbCardSelected : ""
                    }`}
                    onClick={() => handleDbTypeChange(db.id)}
                    style={{
                      flex: "1 1 120px",
                      maxWidth: 150,
                      borderColor:
                        sqlConfig.dbType === db.id ? db.color : undefined,
                    }}
                  >
                    <Storage
                      style={{
                        color: db.color,
                        width: 32,
                        height: 32,
                        marginBottom: 4,
                      }}
                    />
                    <Typography variant="subtitle2">{db.name}</Typography>
                  </Box>
                ))}
              </Box>

              {sqlConfig.dbType !== "sqlite" ? (
                <>
                  <div className={classes.multFieldLine}>
                    <TextField
                      label="Host"
                      value={sqlConfig.host}
                      onChange={(e) =>
                        setSqlConfig({ ...sqlConfig, host: e.target.value })
                      }
                      variant="outlined"
                      margin="dense"
                      placeholder="localhost"
                    />
                    <TextField
                      label="Porta"
                      value={sqlConfig.port}
                      onChange={(e) =>
                        setSqlConfig({ ...sqlConfig, port: e.target.value })
                      }
                      variant="outlined"
                      margin="dense"
                      style={{ maxWidth: 100 }}
                    />
                  </div>

                  <TextField
                    label="Nome do Banco"
                    value={sqlConfig.database}
                    onChange={(e) =>
                      setSqlConfig({ ...sqlConfig, database: e.target.value })
                    }
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    required
                  />

                  <div className={classes.multFieldLine}>
                    <TextField
                      label="Usuário"
                      value={sqlConfig.username}
                      onChange={(e) =>
                        setSqlConfig({ ...sqlConfig, username: e.target.value })
                      }
                      variant="outlined"
                      margin="dense"
                    />
                    <TextField
                      label="Senha"
                      value={sqlConfig.password}
                      onChange={(e) =>
                        setSqlConfig({ ...sqlConfig, password: e.target.value })
                      }
                      variant="outlined"
                      margin="dense"
                      type={showPassword ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              size="small"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </>
              ) : (
                <TextField
                  label="Caminho do Arquivo SQLite"
                  value={sqlConfig.database}
                  onChange={(e) =>
                    setSqlConfig({ ...sqlConfig, database: e.target.value })
                  }
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                  placeholder="/caminho/para/banco.sqlite"
                />
              )}
            </div>
          )}

          {/* TAB 1: Query */}
          {activeTab === 1 && (
            <div className={classes.tabPanel}>
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Tipo de Query</InputLabel>
                <Select
                  label="Tipo de Query"
                  value={sqlConfig.queryType}
                  onChange={(e) =>
                    setSqlConfig({ ...sqlConfig, queryType: e.target.value })
                  }
                >
                  {queryTypes.map((qt) => (
                    <MenuItem key={qt.id} value={qt.id}>
                      {qt.name} - {qt.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {sqlConfig.queryType !== "custom" && (
                <>
                  <TextField
                    label="Nome da Tabela"
                    value={sqlConfig.tableName}
                    onChange={(e) =>
                      setSqlConfig({ ...sqlConfig, tableName: e.target.value })
                    }
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />

                  {(sqlConfig.queryType === "select" ||
                    sqlConfig.queryType === "insert") && (
                    <TextField
                      label="Colunas"
                      value={sqlConfig.columns}
                      onChange={(e) =>
                        setSqlConfig({ ...sqlConfig, columns: e.target.value })
                      }
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      placeholder="* ou col1, col2, col3"
                      helperText="Use * para todas as colunas ou especifique separadas por vírgula"
                    />
                  )}

                  {sqlConfig.queryType !== "insert" && (
                    <TextField
                      label="Cláusula WHERE"
                      value={sqlConfig.whereClause}
                      onChange={(e) =>
                        setSqlConfig({
                          ...sqlConfig,
                          whereClause: e.target.value,
                        })
                      }
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      placeholder="id = ${contactId} AND status = 'ativo'"
                      helperText="Use ${variavel} para substituição de variáveis"
                    />
                  )}
                </>
              )}

              <TextField
                label="Query SQL"
                value={
                  sqlConfig.queryType !== "custom" && !sqlConfig.query
                    ? generateQuery(sqlConfig)
                    : sqlConfig.query
                }
                onChange={(e) =>
                  setSqlConfig({ ...sqlConfig, query: e.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                rows={6}
                className={classes.queryEditor}
                placeholder="SELECT * FROM tabela WHERE id = ${id}"
                helperText="Use ${variavel} para inserir valores dinâmicos"
              />
            </div>
          )}

          {/* TAB 2: Resultado */}
          {activeTab === 2 && (
            <div className={classes.tabPanel}>
              <TextField
                label="Variável de Saída"
                value={sqlConfig.outputVariable}
                onChange={(e) =>
                  setSqlConfig({ ...sqlConfig, outputVariable: e.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                helperText="Nome da variável onde o resultado será armazenado"
              />

              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Retornar</InputLabel>
                <Select
                  label="Retornar"
                  value={sqlConfig.returnFirst ? "first" : "all"}
                  onChange={(e) =>
                    setSqlConfig({
                      ...sqlConfig,
                      returnFirst: e.target.value === "first",
                    })
                  }
                >
                  <MenuItem value="all">Todos os resultados (array)</MenuItem>
                  <MenuItem value="first">Apenas o primeiro resultado</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Timeout (ms)"
                value={sqlConfig.timeout}
                onChange={(e) =>
                  setSqlConfig({
                    ...sqlConfig,
                    timeout: parseInt(e.target.value) || 30000,
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                type="number"
                helperText="Tempo máximo de espera pela resposta"
              />

              <Box
                mt={2}
                p={2}
                style={{
                  backgroundColor:
                    theme.palette.type === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : "rgba(0, 0, 0, 0.02)",
                  borderRadius: 8,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Como usar o resultado:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Acesse via <code>${"{sqlResult}"}</code> em outros nós
                  <br />• Para primeiro registro:{" "}
                  <code>${"{sqlResult[0].coluna}"}</code>
                  <br />• Para contagem: <code>${"{sqlResult.length}"}</code>
                </Typography>
              </Box>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!sqlConfig.queryName || !sqlConfig.database}
          >
            {labels.btn}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddSQLModal;
